
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SecurityProvider } from "@/components/security/SecurityProvider";
import SecureAuthGate from "@/components/auth/SecureAuthGate";
import RequireOrgRole from "@/components/auth/RequireOrgRole";
import RouteLoadingBar from "@/components/layout/RouteLoadingBar";
import Auth0VerificationRedirect from "@/components/auth/Auth0VerificationRedirect";

// Eagerly load high-traffic routes so navigation between them is
// instant (no chunk fetch, no Suspense fallback flicker).
import Index from "./pages/Index";
import Login from "./pages/Login";
import OrgPublicPage from "./pages/public/OrgPublic";
import WelcomeWeekPublicPage from "./pages/ww/WelcomeWeekPublic";
import GroupPublicPage from "./pages/ww/GroupPublic";
import WelcomeWeekAdminPage from "./pages/ww/WelcomeWeekAdmin";
import TeknostartAdminPage from "./pages/ww/TeknostartAdmin";
import GroupsAdminPage from "./pages/ww/GroupsAdmin";

// Lazy load pages for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const EventEditor = lazy(() => import("./pages/EventEditor"));
const EventPage = lazy(() => import("./pages/EventPage"));
const EventAttendees = lazy(() => import("./pages/EventAttendees"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ClerkDemo = lazy(() => import("./pages/ClerkDemo"));
const ClerkLogin = lazy(() => import("./pages/ClerkLogin"));
const Register = lazy(() => import("./pages/Register"));
const OrganizationsPage = lazy(() => import("./pages/Organizations"));
const OrgSettingsPage = lazy(() => import("./pages/org/OrgSettings"));
const OrgMembersPage = lazy(() => import("./pages/org/OrgMembers"));

const AcceptInvitePage = lazy(() => import("./pages/org/AcceptInvite"));
const JoinGroupPage = lazy(() => import("./pages/ww/JoinGroup"));
const JoinOrgPage = lazy(() => import("./pages/org/JoinOrg"));
const AuthCheckEmail = lazy(() => import("./pages/AuthCheckEmail"));
const AuthEmailVerified = lazy(() => import("./pages/AuthEmailVerified"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        if (error?.message?.includes('Invalid API key') ||
            error?.message?.includes('Unauthorized')) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <SecurityProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <BrowserRouter>
              <Auth0VerificationRedirect />
              <Suspense fallback={<RouteLoadingBar />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={
                    <SecureAuthGate requireAuth={false}>
                      <Index />
                    </SecureAuthGate>
                  } />
                  <Route path="/login" element={
                    <SecureAuthGate requireAuth={false}>
                      <Login />
                    </SecureAuthGate>
                  } />
                  <Route path="/register" element={
                    <SecureAuthGate requireAuth={false}>
                      <Register />
                    </SecureAuthGate>
                  } />
                  <Route path="/explore" element={
                    <SecureAuthGate requireAuth={false}>
                      <Dashboard />
                    </SecureAuthGate>
                  } />
                  <Route path="/:slug" element={
                    <SecureAuthGate requireAuth={false}>
                      <EventPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/user/:userId" element={
                    <SecureAuthGate requireAuth={false}>
                      <UserProfilePage />
                    </SecureAuthGate>
                  } />
                  <Route path="/profile/:userId" element={
                    <SecureAuthGate requireAuth={false}>
                      <UserProfilePage />
                    </SecureAuthGate>
                  } />

                  {/* Protected routes */}
                  <Route path="/dashboard" element={
                    <SecureAuthGate requireAuth={true}>
                      <Dashboard />
                    </SecureAuthGate>
                  } />
                  <Route path="/organizations" element={
                    <SecureAuthGate requireAuth={true}>
                      <OrganizationsPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/organizations/:id/settings" element={
                    <SecureAuthGate requireAuth={true}>
                      <RequireOrgRole allow={['owner']} deniedMessage="Only organization owners can access settings.">
                        <OrgSettingsPage />
                      </RequireOrgRole>
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/settings" element={
                    <SecureAuthGate requireAuth={true}>
                      <RequireOrgRole allow={['owner']} deniedMessage="Only organization owners can access settings.">
                        <OrgSettingsPage />
                      </RequireOrgRole>
                    </SecureAuthGate>
                  } />
                  <Route path="/organizations/:id/members" element={
                    <SecureAuthGate requireAuth={true}>
                      <RequireOrgRole allow={['owner', 'admin', 'member']} deniedMessage="You must be a member of this organization to view this page.">
                        <OrgMembersPage />
                      </RequireOrgRole>
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/members" element={
                    <SecureAuthGate requireAuth={true}>
                      <RequireOrgRole allow={['owner', 'admin', 'member']} deniedMessage="You must be a member of this organization to view this page.">
                        <OrgMembersPage />
                      </RequireOrgRole>
                    </SecureAuthGate>
                  } />

                   <Route path="/organizations/invite/:token" element={
                    <SecureAuthGate requireAuth={true}>
                      <AcceptInvitePage />
                    </SecureAuthGate>
                  } />
                  {/* Public organization page and invite acceptance by slug */}
                  <Route path="/o/:slug/ww" element={<WelcomeWeekPublicPage />} />
                  <Route path="/o/:slug/ww/:groupCode" element={<GroupPublicPage />} />
                  <Route path="/o/:slug/ww/admin" element={
                    <SecureAuthGate requireAuth={true}>
                      <WelcomeWeekAdminPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/ww/admin2" element={
                    <SecureAuthGate requireAuth={true}>
                      <WelcomeWeekAdminPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/ww/admin/groups" element={
                    <SecureAuthGate requireAuth={true}>
                      <GroupsAdminPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/ww/admin/central" element={
                    <SecureAuthGate requireAuth={true}>
                      <TeknostartAdminPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/ww/admin/teknostart" element={
                    <SecureAuthGate requireAuth={true}>
                      <TeknostartAdminPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/ww/admin/velkomstuke" element={
                    <SecureAuthGate requireAuth={true}>
                      <TeknostartAdminPage />
                    </SecureAuthGate>
                  } />
                  {/* Per-group admin invite link — public route, handles auth itself */}
                  <Route path="/ww/join/:token" element={<JoinGroupPage />} />
                  {/* Org member/admin self-serve join link — public route, handles auth itself */}
                  <Route path="/o/:slug/join/:token" element={<JoinOrgPage />} />
                  <Route path="/o/:slug" element={<OrgPublicPage />} />
                  <Route path="/o/:slug/invite/:token" element={
                    <SecureAuthGate requireAuth={true}>
                      <AcceptInvitePage />
                    </SecureAuthGate>
                  } />
                  <Route path="/dashboard/:eventId" element={
                    <SecureAuthGate requireAuth={true}>
                      <EventAttendees />
                    </SecureAuthGate>
                  } />
                  <Route path="/dashboard/:eventId/edit" element={
                    <SecureAuthGate requireAuth={true}>
                      <EventEditor />
                    </SecureAuthGate>
                  } />
                  <Route path="/dashboard/new" element={
                    <SecureAuthGate requireAuth={true}>
                      <EventEditor />
                    </SecureAuthGate>
                  } />
                  <Route path="/profile" element={
                    <SecureAuthGate requireAuth={true}>
                      <ProfilePage />
                    </SecureAuthGate>
                  } />
                  <Route path="/clerk-demo" element={
                    <SecureAuthGate requireAuth={true}>
                      <ClerkDemo />
                    </SecureAuthGate>
                  } />
                  <Route path="/clerklogin" element={<ClerkLogin />} />
                  <Route path="/clerk" element={<Navigate to="/clerklogin" replace />} />
                  <Route path="/clerkauth" element={<Navigate to="/clerklogin" replace />} />
                  <Route path="/auth0" element={<Navigate to="/login" replace />} />
                  <Route path="/auth/check-email" element={<AuthCheckEmail />} />
                  <Route path="/auth/email-verified" element={<AuthEmailVerified />} />

                  {/* 404 route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </SecurityProvider>
    </HelmetProvider>
  );
}

export default App;
